<script setup lang="ts">
import { useToastStore } from '../stores/toast'

const toastStore = useToastStore()

const getToastClasses = (type: 'success' | 'warning' | 'error') => {
  const baseClasses = 'flex items-start gap-3 p-4 rounded-lg shadow-lg border-2 min-w-[300px] max-w-[400px] animate-slide-in'

  switch (type) {
    case 'success':
      return `${baseClasses} bg-green-50 border-green-500 text-green-800`
    case 'warning':
      return `${baseClasses} bg-yellow-50 border-yellow-500 text-yellow-800`
    case 'error':
      return `${baseClasses} bg-red-50 border-red-500 text-red-800`
    default:
      return baseClasses
  }
}

const getIconColor = (type: 'success' | 'warning' | 'error') => {
  switch (type) {
    case 'success':
      return 'text-green-600'
    case 'warning':
      return 'text-yellow-600'
    case 'error':
      return 'text-red-600'
    default:
      return 'text-gray-600'
  }
}
</script>

<template>
  <div class="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2">
    <div
      v-for="toast in toastStore.toasts"
      :key="toast.id"
      :class="getToastClasses(toast.type)"
    >
      <!-- Icon -->
      <div class="flex-shrink-0">
        <!-- Success Icon -->
        <svg
          v-if="toast.type === 'success'"
          :class="['w-6 h-6', getIconColor(toast.type)]"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
        </svg>

        <!-- Warning Icon -->
        <svg
          v-else-if="toast.type === 'warning'"
          :class="['w-6 h-6', getIconColor(toast.type)]"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
        </svg>

        <!-- Error Icon -->
        <svg
          v-else-if="toast.type === 'error'"
          :class="['w-6 h-6', getIconColor(toast.type)]"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
        </svg>
      </div>

      <!-- Message -->
      <div class="flex-1 text-sm font-medium">
        {{ toast.message }}
      </div>

      <!-- Close Button -->
      <button
        @click="toastStore.removeToast(toast.id)"
        class="flex-shrink-0 text-gray-500 hover:text-gray-700"
      >
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
        </svg>
      </button>
    </div>
  </div>
</template>

<style scoped>
@keyframes slide-in {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.animate-slide-in {
  animation: slide-in 0.3s ease-out;
}
</style>
